import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SETUP_SAVE_MUTATION_KEY } from '@/core/components/reputation/setup/setup-save-status';
import type {
  AutoReplyConfigDto,
  Location,
  Workspace,
} from '@up2stay/up2promo-api-client';
import { LocationsApi, WorkspacesApi } from '@up2stay/up2promo-api-client';
import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@up2stay/up2promo-ui';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import { useLocations } from '@/core/hooks/use-locations';
import { useWorkspaces } from '@/core/hooks/use-workspaces';
import { useSetWorkspace } from '@/core/store/workspace.store';
import { locationsQueryKeys } from '@/core/query-keys/locations-query-keys';

const DEFAULT_CONFIG: AutoReplyConfigDto = {
  enabled: false,
  minStars: 4,
  minDelayMinutes: 60,
  excludeWithText: false,
  excludeNegativeSentiment: true,
  excludedAspects: [],
  autoPublish: false,
};

export type ReputationAutoReplySettingsDict = {
  enabledLabel?: string;
  enabledDescription?: string;
  minStarsLabel?: string;
  minDelayLabel?: string;
  autoPublishLabel?: string;
  autoPublishDescription?: string;
  excludeWithTextLabel?: string;
  excludeWithTextDescription?: string;
  excludeNegativeSentimentLabel?: string;
  excludeNegativeSentimentDescription?: string;
  save?: string;
  saveSuccess?: string;
  saveError?: string;
  guardrailsHint?: string;
  delayImmediately?: string;
  delayMinutes?: string;
  delayHours?: string;
  delayDay?: string;
};

function formatDelayLabel(
  minutes: number,
  dict?: ReputationAutoReplySettingsDict,
): string {
  if (minutes === 0) {
    return dict?.delayImmediately ?? 'Immediately';
  }
  if (minutes < 60) {
    const template = dict?.delayMinutes ?? '{value} min';
    return template.replace('{value}', String(minutes));
  }
  if (minutes < 1440) {
    const template = dict?.delayHours ?? '{value} h';
    return template.replace('{value}', String(minutes / 60));
  }
  return dict?.delayDay ?? '24 hours';
}

function normalizeConfig(
  config: AutoReplyConfigDto | null | undefined,
): AutoReplyConfigDto {
  if (!config) return { ...DEFAULT_CONFIG };
  return {
    enabled: config.enabled ?? false,
    minStars: config.minStars ?? 4,
    minDelayMinutes: config.minDelayMinutes ?? 60,
    excludeWithText: config.excludeWithText ?? false,
    excludeNegativeSentiment: config.excludeNegativeSentiment ?? true,
    excludedAspects: config.excludedAspects ?? [],
    autoPublish: config.autoPublish ?? false,
  };
}

export function ReputationAutoReplySettings({
  workspaceId,
  scope,
  locationId,
  dict,
  compact = false,
  grouped = false,
}: {
  workspaceId: string;
  scope: 'workspace' | 'location';
  locationId?: string;
  dict?: ReputationAutoReplySettingsDict;
  compact?: boolean;
  /** When true, emit SettingsGroupRows for an outer SettingsSection. */
  grouped?: boolean;
}) {
  const { workspaces } = useWorkspaces();
  const workspace = workspaces.find((w) => w.id === workspaceId);
  const { locations } = useLocations(workspaceId, { limit: 100 });
  const location =
    scope === 'location' && locationId
      ? locations.find((l) => l.id === locationId)
      : null;

  const sourceConfig =
    scope === 'workspace'
      ? workspace?.autoReplyConfig
      : (location?.autoReplyConfig ?? workspace?.autoReplyConfig);

  const initialConfig = normalizeConfig(sourceConfig);
  const remountKey = `${scope}:${locationId ?? 'workspace'}:${JSON.stringify(initialConfig)}`;

  return (
    <ReputationAutoReplySettingsForm
      key={remountKey}
      workspaceId={workspaceId}
      scope={scope}
      locationId={locationId}
      dict={dict}
      compact={compact}
      grouped={grouped}
      initialConfig={initialConfig}
    />
  );
}

function SwitchRow({
  id,
  label,
  description,
  checked,
  onCheckedChange,
  showHint,
}: {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  showHint: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex min-w-0 items-center gap-0.5">
        <Label htmlFor={id} className="text-[13.5px] font-medium">
          {label}
        </Label>
        {showHint && description ? (
          <SettingsInfoHint label={label}>{description}</SettingsInfoHint>
        ) : null}
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

function ReputationAutoReplySettingsForm({
  workspaceId,
  scope,
  locationId,
  dict,
  compact = false,
  grouped = false,
  initialConfig,
}: {
  workspaceId: string;
  scope: 'workspace' | 'location';
  locationId?: string;
  dict?: ReputationAutoReplySettingsDict;
  compact?: boolean;
  grouped?: boolean;
  initialConfig: AutoReplyConfigDto;
}) {
  const queryClient = useQueryClient();
  const setWorkspace = useSetWorkspace();
  const { refetch: refetchWorkspaces } = useWorkspaces();

  const [config, setConfig] = useState<AutoReplyConfigDto>(initialConfig);

  const saveMutation = useMutation({
    mutationKey: SETUP_SAVE_MUTATION_KEY,
    mutationFn: async (payload: AutoReplyConfigDto) => {
      if (scope === 'workspace') {
        await WorkspacesApi.update({
          path: { id: workspaceId },
          body: { autoReplyConfig: payload },
        });
        return;
      }
      if (!locationId) return;
      await LocationsApi.update({
        path: { workspaceId, id: locationId },
        body: { autoReplyConfig: payload },
      });
    },
    onSuccess: async () => {
      if (scope === 'workspace') {
        const refreshed = await refetchWorkspaces();
        const updated = refreshed.find((w) => w.id === workspaceId);
        if (updated) setWorkspace(updated as Workspace);
      } else {
        queryClient.invalidateQueries({
          queryKey: locationsQueryKeys.detail(workspaceId, locationId),
        });
        queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root });
      }
      if (dict?.saveSuccess) toast.success(dict.saveSuccess);
    },
    onError: () => {
      if (dict?.saveError) toast.error(dict.saveError);
    },
  });

  const onSave = () => saveMutation.mutate(config);
  const showHints = !compact;
  const rowId = `${scope}-${locationId ?? 'ws'}`;

  const rows = (
    <SettingsGroupRows>
      <SettingsGroupRow>
        <SwitchRow
          id={`auto-reply-enabled-${rowId}`}
          label={dict?.enabledLabel ?? 'Enable automatic replies'}
          description={dict?.enabledDescription}
          checked={config.enabled ?? false}
          onCheckedChange={(checked) =>
            setConfig((prev) => ({ ...prev, enabled: checked }))
          }
          showHint={showHints}
        />
      </SettingsGroupRow>

      <SettingsGroupRow separated>
        <div className="flex flex-col gap-2 sm:max-w-xs">
          <Label>{dict?.minStarsLabel ?? 'Minimum stars'}</Label>
          <Select
            value={String(config.minStars ?? 4)}
            onValueChange={(value) =>
              setConfig((prev) => ({ ...prev, minStars: Number(value) }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[5, 4, 3, 2, 1].map((stars) => (
                <SelectItem key={stars} value={String(stars)}>
                  {stars} ★
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </SettingsGroupRow>

      {config.autoPublish ? (
        <SettingsGroupRow separated>
          <div className="flex flex-col gap-2 sm:max-w-xs">
            <Label>
              {dict?.minDelayLabel ?? 'Delay before publish (minutes)'}
            </Label>
            <Select
              value={String(config.minDelayMinutes ?? 60)}
              onValueChange={(value) =>
                setConfig((prev) => ({
                  ...prev,
                  minDelayMinutes: Number(value),
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[0, 15, 30, 60, 120, 360, 1440].map((minutes) => (
                  <SelectItem key={minutes} value={String(minutes)}>
                    {formatDelayLabel(minutes, dict)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </SettingsGroupRow>
      ) : null}

      <SettingsGroupRow separated>
        <SwitchRow
          id={`auto-reply-publish-${rowId}`}
          label={dict?.autoPublishLabel ?? 'Auto-publish without approval'}
          description={dict?.autoPublishDescription}
          checked={config.autoPublish ?? false}
          onCheckedChange={(checked) =>
            setConfig((prev) => ({ ...prev, autoPublish: checked }))
          }
          showHint={showHints}
        />
      </SettingsGroupRow>

      <SettingsGroupRow separated>
        <SwitchRow
          id={`auto-reply-exclude-text-${rowId}`}
          label={dict?.excludeWithTextLabel ?? 'Only ratings without a comment'}
          description={dict?.excludeWithTextDescription}
          checked={config.excludeWithText ?? false}
          onCheckedChange={(checked) =>
            setConfig((prev) => ({ ...prev, excludeWithText: checked }))
          }
          showHint={showHints}
        />
      </SettingsGroupRow>

      <SettingsGroupRow separated>
        <SwitchRow
          id={`auto-reply-exclude-sentiment-${rowId}`}
          label={
            dict?.excludeNegativeSentimentLabel ?? 'Exclude negative sentiment'
          }
          description={dict?.excludeNegativeSentimentDescription}
          checked={config.excludeNegativeSentiment ?? true}
          onCheckedChange={(checked) =>
            setConfig((prev) => ({
              ...prev,
              excludeNegativeSentiment: checked,
            }))
          }
          showHint={showHints}
        />
      </SettingsGroupRow>

      <SettingsGroupRow separated>
        <Button onClick={onSave} disabled={saveMutation.isPending}>
          {dict?.save ?? 'Save'}
        </Button>
      </SettingsGroupRow>
    </SettingsGroupRows>
  );

  if (grouped) {
    return rows;
  }

  return (
    <div className="overflow-hidden rounded-[var(--r-settings-group,12px)] bg-card shadow-[var(--shadow-card)]">
      {rows}
    </div>
  );
}

export function hasAutoReplyOverride(location: Location): boolean {
  return location.autoReplyConfig != null;
}
